/**
 * 华奋达全局律动在子应用内点「是」退出：独占 HID，避免 setMusicData / layout / 灯光读写并发卡死。
 */

import { resetRhythmNativeCaptureTracking } from "./rhythmNativeCaptureTracking";

export const HFD_RHYTHM_QUIT_HID_EXCLUSIVE_ENDED_EVENT =
  "hfd-rhythm-quit-hid-exclusive-ended";

declare global {
  interface Window {
    __hfdRhythmQuitHidExclusive?: boolean;
    __musicRhythmQuitRestoreInProgress?: boolean;
    __keyboardDisableMusicRhythmOutput?: boolean;
  }
}

export function isHuafendaRhythmQuitHidExclusive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.__hfdRhythmQuitHidExclusive);
  } catch {
    return false;
  }
}

/** 律动退出恢复全程：停 Bridge 下发 + 禁止 layout/轮询抢 HID（勿设 __musicRhythmQuitRestoreInProgress，避免误伤写灯效） */
export function beginHuafendaRhythmQuitHidExclusive(): void {
  if (typeof window === "undefined") return;
  try {
    window.__hfdRhythmQuitHidExclusive = true;
    window.__keyboardDisableMusicRhythmOutput = true;
    window.electronAudioAPI?.sendAudioControl?.({ type: "STOP_CAPTURE" });
    resetRhythmNativeCaptureTracking();
  } catch {
    // ignore
  }
}

export function endHuafendaRhythmQuitHidExclusive(): void {
  if (typeof window === "undefined") return;
  try {
    window.__hfdRhythmQuitHidExclusive = false;
    window.__keyboardDisableMusicRhythmOutput = false;
    window.__musicRhythmQuitRestoreInProgress = false;
    window.dispatchEvent(
      new CustomEvent(HFD_RHYTHM_QUIT_HID_EXCLUSIVE_ENDED_EVENT)
    );
  } catch {
    // ignore
  }
}

/**
 * X87 V2 有线等：实体 setLEDEffect 校验仍禁律动下发，但先放开 layout/灯效 HID 读供 UI 快照回显。
 * 勿清 __keyboardDisableMusicRhythmOutput，避免校验完成前 Bridge 再次写 setMusicData。
 */
export function releaseHuafendaRhythmQuitHidExclusiveForUiReads(): void {
  if (typeof window === "undefined") return;
  try {
    window.__hfdRhythmQuitHidExclusive = false;
    window.dispatchEvent(
      new CustomEvent(HFD_RHYTHM_QUIT_HID_EXCLUSIVE_ENDED_EVENT)
    );
  } catch {
    // ignore
  }
}

type HfdSerialExecutor = <T>(fn: () => Promise<T>) => Promise<T>;

let hfdLayoutReadChain: Promise<unknown> = Promise.resolve();
let externalHfdSerialExecutor: HfdSerialExecutor | null = null;

/** 主应用 hfdService.runWithHfdLock 注册后，layout getKeyData 与 setMusicData 共用同一 HID 队列 */
export function registerHfdSerialExecutor(executor: HfdSerialExecutor): void {
  externalHfdSerialExecutor = executor;
}

function runLocalHfdLayoutChain<T>(fn: () => Promise<T>): Promise<T> {
  const next = hfdLayoutReadChain.then(fn, fn);
  hfdLayoutReadChain = next.catch(() => undefined);
  return next;
}

/** 华奋达 layout getKeyData 与灯光 HID 读写串行（优先走主应用 hfdOp 队列） */
export function runHfdLayoutKeyDataSerialized<T>(
  fn: () => Promise<T>
): Promise<T> {
  const exec = externalHfdSerialExecutor ?? runLocalHfdLayoutChain;
  return exec(fn);
}

/** 律动退出独占期间轮询等待（最长 timeoutMs） */
export async function waitUntilHuafendaRhythmQuitHidIdle(
  timeoutMs = 30_000
): Promise<boolean> {
  if (!isHuafendaRhythmQuitHidExclusive()) return true;
  return new Promise<boolean>((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener(
        HFD_RHYTHM_QUIT_HID_EXCLUSIVE_ENDED_EVENT,
        onEnded
      );
      resolve(ok);
    };
    const onEnded = () => finish(true);
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    window.addEventListener(HFD_RHYTHM_QUIT_HID_EXCLUSIVE_ENDED_EVENT, onEnded);
    if (!isHuafendaRhythmQuitHidExclusive()) finish(true);
  });
}
